import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api, API_BASE, authHeader, formatApiError } from "@/lib/api";
import { useAdminAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";
import { Download, RefreshCw, LogOut, Trash2 } from "lucide-react";

function StatusBadge({ status, paid }) {
  const label = paid ? "Paid in Full" : (status || "").replaceAll("_", " ");
  const cls = paid
    ? "status-paid"
    : status === "active"
    ? "status-active"
    : status === "waitlist"
    ? "status-waitlist"
    : "status-awaiting_payment";
  return <span className={`text-xs px-2 py-1 rounded-full font-semibold capitalize ${cls}`}>{label}</span>;
}

export default function AdminDashboard() {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [loading, admin, navigate]);

  const [tab, setTab] = useState("registrations");

  if (loading || !admin) {
    return (
      <AppLayout narrow={false}>
        <div className="rrdc-card p-6">Checking session.</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout narrow={false}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-[#2F50A1] font-semibold">Admin</div>
          <h1 className="rrdc-heading text-xl sm:text-3xl font-bold text-[#111827] leading-tight">RRDC Trip Sign Ups Admin</h1>
          <div className="text-xs sm:text-sm text-[#4B5563] truncate">Signed in as {admin.email}</div>
        </div>
        <Button variant="outline" onClick={logout} size="sm" className="rounded-xl shrink-0" data-testid="admin-logout">
          <LogOut size={14} className="mr-1" /> Sign out
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 md:grid-cols-4 mb-4 h-auto" data-testid={TIDS.adminNav}>
          <TabsTrigger value="registrations" className="text-xs sm:text-sm py-2">Registrations</TabsTrigger>
          <TabsTrigger value="trips" className="text-xs sm:text-sm py-2">Trips</TabsTrigger>
          <TabsTrigger value="settings" className="text-xs sm:text-sm py-2">Home &amp; Season</TabsTrigger>
          <TabsTrigger value="campers" className="text-xs sm:text-sm py-2">Campers</TabsTrigger>
        </TabsList>

        <TabsContent value="registrations">
          <RegistrationsPanel />
        </TabsContent>
        <TabsContent value="trips">
          <TripsPanel />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsPanel />
        </TabsContent>
        <TabsContent value="campers">
          <CampersPanel />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}

// -------- Registrations --------
function RegistrationsPanel() {
  const [items, setItems] = useState([]);
  const [tripFilter, setTripFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tripFilter !== "all") params.trip_key = tripFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/admin/registrations", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripFilter, statusFilter]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/registrations/${id}`, patch);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadPdf = async (id) => {
    try {
      const resp = await api.get(`/admin/registrations/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `rrdc_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dl), 5000);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not download PDF");
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete registration for ${name || "this camper"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/registrations/${id}`);
      toast.success("Registration deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/reminders/run");
      toast.success(`Sent ${data.sent} reminder emails`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rrdc-card p-3 sm:p-4">
      <div className="flex flex-wrap gap-2 sm:gap-3 items-end mb-4">
        <div className="min-w-0">
          <Label className="text-xs">Trip</Label>
          <Select value={tripFilter} onValueChange={setTripFilter}>
            <SelectTrigger className="h-10 w-36 sm:w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="broadway">Broadway</SelectItem>
              <SelectItem value="overnight">Overnight</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0">
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-40 sm:w-44 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="waitlist">Waitlist</SelectItem>
              <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} size="sm" className="h-10 rounded-xl">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
        <Button onClick={runReminders} size="sm" className="h-10 rounded-xl rrdc-btn-gold text-xs sm:text-sm">Send monthly reminders</Button>
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3">
        {items.length === 0 ? (
          <div className="text-sm text-[#6B7280] py-4">{loading ? "Loading" : "No registrations yet."}</div>
        ) : items.map((r) => (
          <div
            key={r.id}
            data-testid={TIDS.adminRegRow}
            className="rounded-xl border border-[#E5E7EB] p-3 bg-white space-y-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-[#111827] truncate">{r.camper_name}</div>
                <div className="text-xs text-[#6B7280]">Grade {r.camper_grade} &middot; {r.trip_key === "broadway" ? "Broadway" : "Overnight"}</div>
              </div>
              <StatusBadge status={r.status} paid={r.paid_in_full} />
            </div>
            <div className="text-sm">
              <div className="text-[#111827] truncate">{r.parent_print_name}</div>
              <div className="text-xs text-[#6B7280] truncate">{r.parent_email}</div>
            </div>
            <div className="text-xs text-[#6B7280]">
              Signed: {r.signed_at_est || r.created_at_est || "not signed"}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#F3F4F6]">
              <div>
                <Label className="text-[10px] uppercase text-[#6B7280]">Paid</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Switch
                    checked={!!r.paid_in_full}
                    onCheckedChange={(v) => update(r.id, { paid_in_full: v })}
                    data-testid={TIDS.adminPaidToggle}
                  />
                  <span className="text-xs">{r.paid_in_full ? "Yes" : "No"}</span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-[#6B7280]">Status</Label>
                <Select
                  value={r.status || "awaiting_payment"}
                  onValueChange={(v) => update(r.id, { status: v })}
                >
                  <SelectTrigger className="h-8 bg-white mt-1" data-testid={TIDS.adminStatusToggle}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="waitlist">Waitlist</SelectItem>
                    <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadPdf(r.id)}
                className="rounded-lg flex-1"
                data-testid={TIDS.adminDownloadPdf}
                disabled={!r.signed_at}
              >
                <Download size={14} className="mr-1" /> PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => del(r.id, r.camper_name)}
                className="rounded-lg flex-1 text-red-600 border-red-200 hover:bg-red-50"
                data-testid="admin-delete-registration"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: full table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-[#6B7280]">
            <tr>
              <th className="py-2 pr-4">Signed EST</th>
              <th className="py-2 pr-4">Trip</th>
              <th className="py-2 pr-4">Camper</th>
              <th className="py-2 pr-4">Parent</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Method</th>
              <th className="py-2 pr-4">Paid</th>
              <th className="py-2 pr-4">Move</th>
              <th className="py-2 pr-4">PDF</th>
              <th className="py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td className="py-4 text-[#6B7280]" colSpan={10}>{loading ? "Loading" : "No registrations yet."}</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E7EB]" data-testid={TIDS.adminRegRow}>
                <td className="py-2 pr-4 whitespace-nowrap">{r.signed_at_est || r.created_at_est}</td>
                <td className="py-2 pr-4 capitalize">{r.trip_key}</td>
                <td className="py-2 pr-4">
                  <div className="font-semibold text-[#111827]">{r.camper_name}</div>
                  <div className="text-xs text-[#6B7280]">Grade {r.camper_grade}</div>
                </td>
                <td className="py-2 pr-4">
                  <div>{r.parent_print_name}</div>
                  <div className="text-xs text-[#6B7280]">{r.parent_email}</div>
                </td>
                <td className="py-2 pr-4"><StatusBadge status={r.status} paid={r.paid_in_full} /></td>
                <td className="py-2 pr-4 text-xs capitalize">{(r.payment_method || "").replace("_", " or ")}</td>
                <td className="py-2 pr-4">
                  <Switch
                    checked={!!r.paid_in_full}
                    onCheckedChange={(v) => update(r.id, { paid_in_full: v })}
                    data-testid={TIDS.adminPaidToggle}
                  />
                </td>
                <td className="py-2 pr-4">
                  <Select
                    value={r.status || "awaiting_payment"}
                    onValueChange={(v) => update(r.id, { status: v })}
                  >
                    <SelectTrigger className="h-8 w-36 bg-white" data-testid={TIDS.adminStatusToggle}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="waitlist">Waitlist</SelectItem>
                      <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2 pr-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadPdf(r.id)}
                    className="rounded-lg"
                    data-testid={TIDS.adminDownloadPdf}
                    disabled={!r.signed_at}
                  >
                    <Download size={14} className="mr-1" /> PDF
                  </Button>
                </td>
                <td className="py-2 pr-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => del(r.id, r.camper_name)}
                    className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="admin-delete-registration"
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------- Trips (settings + flyer + open/close) --------
function TripsPanel() {
  const [trips, setTrips] = useState([]);
  useEffect(() => { api.get("/admin/trips").then((r) => setTrips(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {trips.map((t) => <TripEditor key={t.key} initial={t} onSaved={(nt) => setTrips((prev) => prev.map((x) => x.key === nt.key ? nt : x))} />)}
    </div>
  );
}

function TripEditor({ initial, onSaved }) {
  const [t, setT] = useState({ ...initial, price_dollars: ((initial.price_cents || 0) / 100).toFixed(2) });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setT((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const dollars = parseFloat(t.price_dollars);
      const priceCents = isNaN(dollars) ? 0 : Math.round(dollars * 100);
      const patch = {
        display_name: t.display_name,
        price_cents: priceCents,
        capacity_total: Number(t.capacity_total) || 0,
        active_cutoff: Number(t.active_cutoff) || 0,
        payment_deadline_date: t.payment_deadline_date || null,
        trip_start_date: t.trip_start_date || null,
        trip_end_date: t.trip_end_date || null,
        permission_statement: t.permission_statement,
        permission_body: t.permission_body || "",
        detail_show_name: t.detail_show_name || null,
        detail_city: t.detail_city || null,
        detail_state: t.detail_state || null,
      };
      const { data } = await api.put(`/admin/trips/${t.key}`, patch);
      setT({ ...data, price_dollars: ((data.price_cents || 0) / 100).toFixed(2) });
      onSaved && onSaved(data);
      toast.success("Trip saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleOpen = async () => {
    try {
      const { data } = await api.post(`/admin/trips/${t.key}/${t.is_open ? "close" : "open"}`);
      setT({ ...data, price_dollars: ((data.price_cents || 0) / 100).toFixed(2) });
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadFlyer = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/trips/${t.key}/flyer`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setT({ ...data, price_dollars: ((data.price_cents || 0) / 100).toFixed(2) });
      onSaved && onSaved(data);
      toast.success("Flyer uploaded");
      setFile(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rrdc-card p-3 sm:p-5">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-[#2F50A1] font-semibold">{t.key}</div>
          <div className="rrdc-heading text-base sm:text-xl font-bold break-words">{t.display_name}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs px-2 py-1 rounded-full ${t.is_open ? "status-active" : "status-awaiting_payment"} font-semibold`}>
            {t.is_open ? "Open" : "Closed"}
          </span>
          <Switch
            checked={!!t.is_open}
            onCheckedChange={toggleOpen}
            data-testid={t.is_open ? TIDS.adminCloseTrip : TIDS.adminOpenTrip}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div>
          <Label className="text-xs">Display name</Label>
          <Input value={t.display_name || ""} onChange={(e) => set("display_name", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Price (US dollars)</Label>
          <Input
            type="number"
            step="0.01"
            value={t.price_dollars}
            onChange={(e) => set("price_dollars", e.target.value)}
            placeholder="125.00"
          />
        </div>
        <div>
          <Label className="text-xs">Capacity total</Label>
          <Input type="number" value={t.capacity_total || 0} onChange={(e) => set("capacity_total", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Active cutoff</Label>
          <Input type="number" value={t.active_cutoff || 0} onChange={(e) => set("active_cutoff", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Payment deadline (Eastern Time, optional)</Label>
          <Input
            type="date"
            value={t.payment_deadline_date || ""}
            onChange={(e) => set("payment_deadline_date", e.target.value)}
            data-testid="admin-payment-deadline-date"
          />
          <div className="text-[11px] text-[#6B7280] mt-1">
            Auto-locks to <strong>11:59 PM Eastern Time</strong> on this date. Leave empty for no deadline.
            {t.payment_deadline_est ? <span> Current: <strong>{t.payment_deadline_est}</strong>.</span> : null}
          </div>
        </div>
        {t.key === "broadway" ? (
          <>
            <div>
              <Label className="text-xs">Trip date (Eastern)</Label>
              <Input
                type="date"
                value={t.trip_start_date || ""}
                onChange={(e) => set("trip_start_date", e.target.value)}
                data-testid="admin-trip-start-date"
              />
              <div className="text-[11px] text-[#6B7280] mt-1">Auto-updates the permission slip date line.</div>
            </div>
            <div>
              <Label className="text-xs">Broadway show name</Label>
              <Input value={t.detail_show_name || ""} onChange={(e) => set("detail_show_name", e.target.value)} />
            </div>
          </>
        ) : (
          <>
            <div>
              <Label className="text-xs">Trip start date (Eastern)</Label>
              <Input
                type="date"
                value={t.trip_start_date || ""}
                onChange={(e) => set("trip_start_date", e.target.value)}
                data-testid="admin-trip-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">Trip end date (Eastern)</Label>
              <Input
                type="date"
                value={t.trip_end_date || ""}
                onChange={(e) => set("trip_end_date", e.target.value)}
                data-testid="admin-trip-end-date"
              />
            </div>
            <div>
              <Label className="text-xs">Overnight city</Label>
              <Input value={t.detail_city || ""} onChange={(e) => set("detail_city", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Overnight state</Label>
              <Input value={t.detail_state || ""} onChange={(e) => set("detail_state", e.target.value)} />
            </div>
            <div className="sm:col-span-2 text-[11px] text-[#6B7280]">
              Trip dates auto-update the &quot;Date of Travel&quot; line in the permission slip.
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <Label className="text-xs">Permission body (full descriptive text)</Label>
          <div className="text-[11px] text-[#6B7280] mb-1">
            This is the exact text parents see on the sign step and in the confirmation email. Trip dates are pulled from the date pickers above and injected wherever you leave the tokens <code>{`{{TRIP_DATE}}`}</code>, <code>{`{{TRIP_DATE_RANGE}}`}</code>, or <code>{`{{SHOW_NAME}}`}</code>. Edit anything else here directly.
          </div>
          <Textarea
            rows={22}
            value={t.permission_body || ""}
            onChange={(e) => set("permission_body", e.target.value)}
            placeholder="Full text from the paper permission slip, verbatim"
            className="font-mono text-xs"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Permission statement</Label>
          <Textarea rows={3} value={t.permission_statement || ""} onChange={(e) => set("permission_statement", e.target.value)} />
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button onClick={save} disabled={busy} className="rrdc-btn-primary rounded-xl" data-testid={TIDS.adminSaveTrip}>
          {busy ? "Saving" : "Save"}
        </Button>
      </div>

      <div className="mt-6 border-t border-[#E5E7EB] pt-4">
        <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold">Flyer</div>
        <div className="text-xs text-[#4B5563] mt-1">
          JPEG, PNG, WEBP or PDF. Shown at the bottom of the parent form and attached to generated PDFs.
        </div>
        <div className="flex flex-wrap gap-2 mt-2 items-center">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files[0] || null)}
            data-testid={TIDS.adminFlyerUpload}
          />
          <Button onClick={uploadFlyer} disabled={!file} className="rrdc-btn-gold rounded-xl">Upload flyer</Button>
        </div>
        {t.flyer_original_filename ? (
          <div className="mt-2 text-xs text-[#4B5563]">Current: {t.flyer_original_filename}</div>
        ) : null}
      </div>
    </div>
  );
}

// -------- Home + Season --------
function SettingsPanel() {
  const [home, setHome] = useState(null);
  const [season, setSeason] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/home").then((r) => setHome(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/settings/season").then((r) => setSeason(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const saveHome = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/home", home);
      setHome(data);
      toast.success("Home page saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSeason = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/season", { active_season_id: season.active_season_id });
      setSeason(data);
      toast.success("Season updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/campminder/sync");
      toast.success(`CampMinder sync: synced ${data.synced} eligible camper(s).`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  if (!home || !season) return <div className="rrdc-card p-4">Loading.</div>;

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rrdc-card p-5">
        <div className="rrdc-heading text-xl font-bold">Home page wording</div>
        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs">Heading</Label>
            <Input value={home.heading || ""} onChange={(e) => setHome({ ...home, heading: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Subheading</Label>
            <Textarea rows={2} value={home.subheading || ""} onChange={(e) => setHome({ ...home, subheading: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Footer note</Label>
            <Input value={home.footer_note || ""} onChange={(e) => setHome({ ...home, footer_note: e.target.value })} />
          </div>
        </div>
        <Button onClick={saveHome} disabled={busy} className="mt-4 rrdc-btn-primary rounded-xl" data-testid={TIDS.adminSaveHome}>
          {busy ? "Saving" : "Save home page"}
        </Button>
      </div>

      <div className="rrdc-card p-5">
        <div className="rrdc-heading text-xl font-bold">Season changeover</div>
        <p className="text-sm text-[#4B5563] mt-1 no-hyphens">
          CampMinder sync and eligibility use this season id. Change to the next season only when you are ready.
        </p>
        <div className="mt-4">
          <Label className="text-xs">Active season id</Label>
          <Input
            value={season.active_season_id || ""}
            onChange={(e) => setSeason({ ...season, active_season_id: e.target.value })}
          />
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={saveSeason} disabled={busy} className="rrdc-btn-primary rounded-xl" data-testid={TIDS.adminSaveSeason}>
            {busy ? "Saving" : "Save season"}
          </Button>
          <Button onClick={runSync} disabled={syncing} className="rrdc-btn-gold rounded-xl" data-testid={TIDS.adminSyncCampminder}>
            {syncing ? "Syncing" : "Sync from CampMinder"}
          </Button>
        </div>
        <div className="text-xs text-[#6B7280] mt-2">
          Sync fetches only current season Navigators and CITs. If CampMinder credentials are not set, this returns configured false.
        </div>
      </div>

      <div className="rrdc-card p-5 md:col-span-2">
        <ParentGatePanel />
      </div>
    </div>
  );
}

function ParentGatePanel() {
  const [pw, setPw] = useState("");
  const [current, setCurrent] = useState("");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    api.get("/admin/parent-gate")
      .then((r) => { setCurrent(r.data.password || ""); setPw(r.data.password || ""); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    if (!pw.trim() || pw.trim().length < 4) {
      toast.error("Access code must be at least 4 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/admin/parent-gate", { password: pw.trim() });
      setCurrent(data.password);
      toast.success("Parent access code updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="rrdc-heading text-xl font-bold">Parent access code</div>
      <p className="text-sm text-[#4B5563] mt-1 no-hyphens">
        Parents must enter this code before they can view any trip sign up page. Share it with the parents you want to invite. Change it any time and previously issued sessions stay active until they expire (12 hours).
      </p>
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Access code</Label>
          <div className="flex gap-2">
            <Input
              type={reveal ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="e.g. Parent2026"
              data-testid="admin-parent-gate-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setReveal((r) => !r)}
              className="rounded-xl"
            >
              {reveal ? "Hide" : "Show"}
            </Button>
          </div>
          <div className="text-[11px] text-[#6B7280] mt-1">
            Current code on file: <strong>{current || "(none)"}</strong>
          </div>
        </div>
        <div className="flex items-end">
          <Button
            onClick={save}
            disabled={busy || pw.trim() === current}
            className="rrdc-btn-primary rounded-xl"
            data-testid="admin-parent-gate-save"
          >
            {busy ? "Saving" : "Save access code"}
          </Button>
        </div>
      </div>
    </>
  );
}

// -------- Campers --------
function CampersPanel() {
  const [state, setState] = useState({ season_id: "", campers: [] });
  const [form, setForm] = useState({ first_name: "", last_name: "", division: "Navigator", grade_entering: "", parent_email: "", parent_name: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/campers");
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/admin/campers/manual", form);
      setForm({ first_name: "", last_name: "", division: "Navigator", grade_entering: "", parent_email: "", parent_name: "" });
      load();
      toast.success("Camper added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/campers/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="rrdc-card p-3 sm:p-5 md:col-span-1">
        <div className="rrdc-heading text-lg sm:text-xl font-bold">Add camper manually</div>
        <p className="text-xs text-[#4B5563] mt-1 no-hyphens">
          Use this before CampMinder is wired, or to add a special case. Only Navigators and CITs are eligible.
        </p>
        <div className="mt-4 space-y-3">
          <Input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <Select value={form.division} onValueChange={(v) => setForm({ ...form, division: v })}>
            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="Navigator">Navigator</SelectItem>
              <SelectItem value="CIT">CIT</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Grade entering" value={form.grade_entering} onChange={(e) => setForm({ ...form, grade_entering: e.target.value })} />
          <Input placeholder="Parent name" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} />
          <Input placeholder="Parent email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} />
          <Button onClick={add} disabled={busy || !form.first_name || !form.last_name} className="w-full rrdc-btn-primary rounded-xl" data-testid={TIDS.adminAddManualCamper}>
            {busy ? "Adding" : "Add camper"}
          </Button>
        </div>
      </div>

      <div className="rrdc-card p-3 sm:p-5 md:col-span-2">
        <div className="rrdc-heading text-lg sm:text-xl font-bold">Roster ({state.campers.length})</div>
        <div className="text-xs text-[#4B5563] mt-1">Season {state.season_id}</div>

        {/* Mobile: card list */}
        <div className="md:hidden mt-3 space-y-2">
          {state.campers.length === 0 ? (
            <div className="text-sm text-[#6B7280] py-4">No campers yet. Add manually above, or run a CampMinder sync.</div>
          ) : state.campers.map((c) => (
            <div key={c.id} className="rounded-xl border border-[#E5E7EB] p-3 bg-white" data-testid="admin-camper-card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-[#111827] truncate">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-[#6B7280]">{c.division} &middot; Grade {c.grade_entering || "-"}</div>
                  <div className="text-[11px] text-[#4B5563] mt-1 font-mono" data-testid="admin-camper-person-id">
                    PersonID: {c.person_id || c.campminder_person_id || "-"}
                    {c.person_id_source && c.person_id_source !== "campminder" ? (
                      <span className="ml-1 text-[#6B7280]">({c.person_id_source})</span>
                    ) : null}
                  </div>
                  {c.parent_email ? <div className="text-xs text-[#4B5563] mt-1 truncate">{c.parent_email}</div> : null}
                </div>
                <Button size="sm" variant="outline" onClick={() => remove(c.id)} className="rounded-lg shrink-0">Remove</Button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280]">
              <tr>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">PersonID</th>
                <th className="py-2 pr-4">Division</th>
                <th className="py-2 pr-4">Grade</th>
                <th className="py-2 pr-4">Parent</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {state.campers.length === 0 ? (
                <tr><td colSpan={6} className="py-4 text-[#6B7280]">No campers yet. Add manually above, or run a CampMinder sync.</td></tr>
              ) : state.campers.map((c) => (
                <tr key={c.id} className="border-t border-[#E5E7EB]" data-testid="admin-camper-row">
                  <td className="py-2 pr-4 font-semibold">{c.first_name} {c.last_name}</td>
                  <td className="py-2 pr-4 font-mono text-xs" data-testid="admin-camper-person-id">
                    {c.person_id || c.campminder_person_id || "-"}
                    {c.person_id_source && c.person_id_source !== "campminder" ? (
                      <span className="ml-1 text-[#6B7280]">({c.person_id_source})</span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4">{c.division}</td>
                  <td className="py-2 pr-4">{c.grade_entering}</td>
                  <td className="py-2 pr-4 text-xs">{c.parent_name}<br/>{c.parent_email}</td>
                  <td className="py-2 pr-4">
                    <Button size="sm" variant="outline" onClick={() => remove(c.id)} className="rounded-lg">Remove</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
